import HmacSHA256 from 'crypto-js/hmac-sha256';
import {
    IOrderInfo,
    ISymbol,
    ITicker,
    Market,
    OrderState
} from './market-base.class';
import { IMarketSettingsToken } from "./IMarketSettingsToken";

export class MarketBithumb extends Market<IMarketSettingsToken> {
    protected name: string = 'BitHumb Global';
    protected icon: string = '/assets/images/exchanges/mxc.svg';

    private accessUrl: string = 'https://global-openapi.bithumb.pro/openapi/v1';

    // private proxyApi: string = '/api/market/mxc';

    private currencies: string[] = [];
    private symbols: ISymbol[] = [];

    private prefferedCurrencies: string[] = ['USDT', 'JASMY', 'ETH'];


    public async checkConnection(): Promise<boolean> {
        const queryString = this.createQueryString();
        const endpoint = '/serverTime';

        // const preSignedText = this.getPreSignedText(queryString);

        // const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}`;

        try {
            const response = await this.http.get(url).toPromise();

            if (response['code'] === 0) {
                return true;
            }

            return false;
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public async getCurrencies(): Promise<string[]> {
        if (this.currencies.length > 0) {
            return this.currencies;
        }

        // const queryString = this.createQueryString();
        const endpoint = '/spot/config';

        // const preSignedText = this.getPreSignedText(queryString);

        // const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}`;

        try {
            const response = await this.http.get(url).toPromise();

            if (response['msg'] === 'success') {
                const coins = response['data'].coinConfig.map(
                    ({ name }) => name
                );
                // .filter(_symbol => this.allowedSymbols.includes(_symbol));

                return this.sortCurrencies(coins);
            } else {
                throw new Error(response['message']);
            }

            return [];
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public async getSymbols(_currency: string = ''): Promise<ISymbol[]> {
        const cachedSymbols = this.symbols.filter((_symbol) =>
            _symbol.symbol.toLowerCase().includes(_currency.toLowerCase())
        );

        if (cachedSymbols.length > 0) {
            return cachedSymbols;
        }

        // const queryString = this.createQueryString();
        const endpoint = '/spot/config';

        // const preSignedText = this.getPreSignedText(queryString);

        // const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}`;

        try {
            const response = await this.http.get(url).toPromise();

            if (response['msg'] === 'success') {
                return (
                    response['data'].spotConfig
                        // .filter(({symbol}) => this.allowedSymbols.includes(symbol))
                        .filter((_symbol) =>
                            _symbol.symbol
                                .toLowerCase()
                                .includes(_currency.toLowerCase())
                        )
                        .map((_symbol) => this.parseSymbol(_symbol))
                );
            } else {
                throw new Error(response['message']);
            }

            return [];
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public async getBalance(_currency: string): Promise<string> {
        const timestamp = Date.now();
        const queryString = this.createQueryString({
            apiKey: this.settings.access_key,
            assetType: 'spot',
            timestamp,
        });
        const endpoint = '/spot/assetList';

        const preSignedText = this.getPreSignedText(queryString);

        const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`;

        try {
            const response = await this.http
                .post(url, {
                    apiKey: this.settings.access_key,
                    assetType: 'spot',
                    signature: signature,
                    timestamp,
                })
                .toPromise();

            if (response['msg'] === 'success') {
                const wallet = response['data'].find(
                    (_asset) =>
                        _asset.coinType.toUpperCase() ===
                        _currency.toUpperCase()
                );

                if (Boolean(wallet)) {
                    return wallet['count'];
                }

                return '0.00';
            } else {
                throw new Error(response['message']);
            }

            return '0.00';
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public async getTicker(_symbol: string): Promise<ITicker> {
        const queryString = this.createQueryString({
            symbol: _symbol.toUpperCase(),
        });
        const endpoint = '/spot/orderBook';

        const preSignedText = this.getPreSignedText(queryString);

        // const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}?${queryString}`;

        try {
            const response = await this.http.get(url).toPromise();

            if (response['msg'] === 'success') {
                return {
                    buy: response['data']['b'][0][0],
                    sell: response['data']['s'][0][0],
                };
            } else {
                throw new Error(response['message']);
            }

            return null;
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public getOrderInfoUrl(_orderId: string, _symbol?: string) {
        const endpoint = '/spot/singleOrder';

        const queryString = this.createQueryString();

        const preSignedText = this.getPreSignedText(queryString);

        const signature = this.getQuerySignature(preSignedText);

        return {url: `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`, data: null};
    }

    public async getOrderInfo(
        _orderId: string,
        _symbol: string
    ): Promise<IOrderInfo> {
        const endpoint = '/spot/singleOrder';

        const timestamp = Date.now();

        const queryString = this.createQueryString({
            apiKey: this.settings.access_key,
            orderId: _orderId,
            symbol: _symbol.toUpperCase(),
            timestamp,
        });

        const preSignedText = this.getPreSignedText(queryString);

        const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`;

        try {
            const response = await this.http
                .post(url, {
                    apiKey: this.settings.access_key,
                    orderId: _orderId,
                    symbol: _symbol.toUpperCase(),
                    timestamp: timestamp.toString(),
                    signature
                })
                .toPromise();

            if (response['msg'] === 'success') {
                const orderData = response['data'];

                if (!orderData) {
                    throw new Error('ORDER_NOT_FOUND');
                }

                return this.parseOrderInfo(orderData);
            } else {
                throw new Error(response['message']);
            }

            return null;
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public async getOrdersHistory(_symbol): Promise<IOrderInfo[]> {
        if (!_symbol) {
            return [];
        }

        try {
            const ordersDataBuy = await this.getOrdersHistoryBuy(_symbol);
            const ordersDataSell = await this.getOrdersHistorySell(_symbol);

            return [...ordersDataBuy, ...ordersDataSell]
                .sort((a, b) => {
                    if (a['createTime'] > b['createTime']) return -1;
                    if (a['createTime'] < b['createTime']) return 1;

                    return 0;
                })
                .map((_orderData) => this.parseOrderInfo(_orderData));
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    private async getOrdersHistoryBuy(_symbol: string) {
        const endpoint = '/spot/orderList';

        const timestamp = Date.now();

        const queryString = this.createQueryString({
            apiKey: this.settings.access_key,
            side: 'buy',
            symbol: _symbol.toUpperCase(),
            status: 'success',
            queryRange: 'thisweek',
            timestamp: timestamp,
        });

        const preSignedText = this.getPreSignedText(queryString);

        const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`;

        try {
            const response = await this.http
                .post(url, {
                    apiKey: this.settings.access_key,
                    side: 'buy',
                    symbol: _symbol.toUpperCase(),
                    status: 'success',
                    queryRange: 'thisweek',
                    timestamp: timestamp.toString(),
                    signature,
                })
                .toPromise();

            if (response['msg'] === 'success') {
                let ordersData = response['data']['list'];

                return ordersData;
            } else {
                throw new Error(response['message']);
            }
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    private async getOrdersHistorySell(_symbol: string) {
        const endpoint = '/spot/orderList';

        const timestamp = Date.now();

        const queryString = this.createQueryString({
            apiKey: this.settings.access_key,
            side: 'sell',
            symbol: _symbol.toUpperCase(),
            status: 'success',
            queryRange: 'thisweek',
            timestamp: timestamp,
        });

        const preSignedText = this.getPreSignedText(queryString);

        const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`;

        try {
            const response = await this.http
                .post(url, {
                    apiKey: this.settings.access_key,
                    side: 'sell',
                    symbol: _symbol.toUpperCase(),
                    status: 'success',
                    queryRange: 'thisweek',
                    timestamp: timestamp.toString(),
                    signature,
                })
                .toPromise();

            if (response['msg'] === 'success') {
                let ordersData = response['data']['list'];

                return ordersData;
            } else {
                throw new Error(response['message']);
            }
        } catch (error) {
            console.error(error);

            throw error;
        }
    }

    public async createOrder(
        _symbol: string,
        _amount: string,
        _type: 'buy' | 'sell'
    ): Promise<string> {
        const endpoint = '/spot/placeOrder';

        const timestamp = Date.now();
        const msgNo = '12345678910';

        const queryString = this.createQueryString({
            apiKey: this.settings.access_key,
            symbol: _symbol,
            side: _type,
            type: 'market',
            quantity: _amount,
            price: -1,
            msgNo,
            timestamp: timestamp,
        });

        const preSignedText = this.getPreSignedText(queryString);

        const signature = this.getQuerySignature(preSignedText);

        const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`;

        try {
            const response = await this.http
                .post(url, {
                    apiKey: this.settings.access_key,
                    symbol: _symbol,
                    side: _type,
                    type: 'market',
                    quantity: _amount,
                    price: '-1',
                    timestamp: timestamp.toString(),
                    msgNo,
                    signature,
                })
                .toPromise();

            if (response['msg'] === 'success') {
                return response['data']['orderId'];
            } else {
                throw new Error(this.parseOrderResponse(response));
            }
        } catch (error) {
            // console.error(error);

            throw error;
        }
    }
   
    private parseOrderInfo(_orderInfo: any): IOrderInfo {
        const sendAmount =
            _orderInfo['side'] === 'buy'
                ? _orderInfo['tradeTotal']
                : _orderInfo['tradedNum'];
        const getAmount =
            _orderInfo['side'] === 'buy'
                ? _orderInfo['tradedNum']
                : _orderInfo['tradeTotal'];

        const currencies = this.coinsService.parseExchangeSymbol(
            _orderInfo['symbol']
        );

        return {
            orderId: _orderInfo['orderId'],
            sendAmount: sendAmount.toString(),
            getAmount: getAmount.toString(),
            fee: null,
            finishedAt: _orderInfo['createTime'].toString(),
            price: _orderInfo['avgPrice'],
            state: this.parseOrderState(_orderInfo['status']),
            type: _orderInfo['side'],
            symbol: _orderInfo['symbol'],
            currencySend:
                _orderInfo['side'] === 'buy' ? currencies[1] : currencies[0],
            currencyGet:
                _orderInfo['side'] === 'buy' ? currencies[0] : currencies[1],
        };
    }

    private parseOrderState(_orderStatus: string): OrderState {
        if (['pending', 'send'].includes(_orderStatus)) {
            return 'processing';
        }

        if (['success'].includes(_orderStatus)) {
            return 'complete';
        }

        if (['cancel'].includes(_orderStatus)) {
            return 'canceled';
        }

        return 'failed';
    }

    private parseOrderResponse(_response: any) {
        if (Boolean(_response['message'])) {
            return _response['message'];
        }

        if (_response['code'] === 30004) {
            return 'EXCHANGE.ERRORS.INSUFFICIENT-FUNDS';
        }

        return 'FORM.ERRORS.SMTH-WENT-WRONG';
    }

    private createQueryString(_params: Object = {}) {
        const defaultParams = {
            apiKey: this.settings.access_key,
            // timestamp: Date.now(),
        };

        const params = { ...defaultParams, ..._params };

        const ordered = Object.keys(params).sort();

        let result = '';

        ordered.forEach((_key) => {
            result += `&${_key}=${params[_key]}`;
        });

        return result.slice(1);
    }

    private getPreSignedText(_queryParams: string) {
        return `${_queryParams}`;
    }

    private getQuerySignature(_query: string) {
        const hash = HmacSHA256(_query, this.settings.secret_key);

        return hash.toString();
    }

    private symbolsToCurrencies(_symbols: string[]): string[] {
        const currenciesSet: Set<string> = new Set();

        _symbols.forEach((_symbol) => {
            const currencies = this.coinsService.parseExchangeSymbol(_symbol);
            currenciesSet.add(currencies[0]);
            currenciesSet.add(currencies[1]);
        });

        let currencies = Array.from(currenciesSet);

        currencies = currencies.sort((a, b) => {
            const indexA = this.prefferedCurrencies.indexOf(a);
            const indexB = this.prefferedCurrencies.indexOf(b);
            if (indexA === -1) return 0;

            if (indexA < indexB) return -1;

            if (indexA !== -1 && indexB === -1) return -1;

            if (indexA > indexB) return 1;

            return 0;
        });

        return currencies;
    }

    private sortCurrencies(_currencies: string[]): string[] {
        return _currencies.sort((a, b) => {
            const indexA = this.prefferedCurrencies.indexOf(a);
            const indexB = this.prefferedCurrencies.indexOf(b);
            if (indexA === -1) return 0;

            if (indexA < indexB) return -1;

            if (indexA !== -1 && indexB === -1) return -1;

            if (indexA > indexB) return 1;

            return 0;
        });
    }

    private parseSymbol(_symbolData: any): ISymbol {
        const currencies = this.coinsService.parseExchangeSymbol(
            _symbolData.symbol
        );
        return {
            symbol: _symbolData.symbol,
            base_currency: currencies[0],
            quote_currency: currencies[1],
            precision: Math.max(..._symbolData['accuracy']).toString(),
        };
    }
}
